import { FC } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Box, Card, CircularProgress, MenuItem, Typography } from '@mui/material';
import Dropdown from 'components/Dropdown';
import { deleteScheduledEstimateTournaments, getScheduledEstimateTournaments, newScheduledEstimateTournaments } from 'api/schedulersettings';

type Props = {
  /**
   * Hash турнира.
   */
  hash: string;
}

/**
 * Отображает таблицу ручной оценки турниров в расписаторе.
 */
const EstimateTable: FC<Props> = ({ hash }) => {
    const queryClient = useQueryClient();

    const {data: tournaments, isFetching} = useQuery(['scheduler-estimate-tournaments-data', hash], getScheduledEstimateTournaments, {
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        retry: false,
    },)

    const { mutate: mutateNewEstimate } = useMutation(newScheduledEstimateTournaments, {
        onSuccess: () => {
            queryClient.invalidateQueries('scheduler-estimate-tournaments-data');
            queryClient.invalidateQueries('scheduler-tournaments-data');
        },
    });

    const { mutate: mutateDeleteEstimate } = useMutation(deleteScheduledEstimateTournaments, {
        onSuccess: () => {
            queryClient.invalidateQueries('scheduler-estimate-tournaments-data');
            queryClient.invalidateQueries('scheduler-tournaments-data');
        },
    });

    function checkValue(day: number) {
        return tournaments?.scorings?.filter(data => data.day_number === day)[0]?.t_eval ?? '—';
    }

    const rate = [-2, -1, 0, 1, 2];
    const days = [
        {
            day_number: 0,
            name: 'Понедельник',
            value: checkValue(0),
        },
        {
            day_number: 1,
            name: 'Вторник',
            value: checkValue(1),
        },
        {
            day_number: 2,
            name: 'Среда',
            value: checkValue(2),
        },
        {
            day_number: 3,
            name: 'Четверг',
            value: checkValue(3),
        },
        {
            day_number: 4,
            name: 'Пятница',
            value: checkValue(4),
        },
        {
            day_number: 5,
            name: 'Суббота',
            value: checkValue(5),
        },
        {
            day_number: 6,
            name: 'Воскресенье',
            value: checkValue(6),
        }
    ]

    function editItem(day: number, value: number | string, close: Function ) {
        mutateNewEstimate({ tournament_group_hash: hash , day_number: day, t_eval: value });
        close();
    }

    function deleteItem(day: number, close: Function, oldValue: number | string) {
        if (oldValue !== '—') {
            const rowId = tournaments?.scorings?.filter(data => data.day_number === day)[0]?.id;
            mutateDeleteEstimate(rowId);
        }
        close();
    }

    return (
        <>
            {isFetching && !tournaments && <Card sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}><CircularProgress /></Card>}
            {tournaments &&
                <Box className='px-4 py-2'>
                    <Typography>Оценка турнира куратором</Typography>
                    <Box className='flex flex-row flex-wrap gap-4 mt-2 xl:justify-between'>
                        {days.map((val) => (
                            <Dropdown
                                btnLabel={val.name}
                                btnValue={String(val.value)}
                                key={val.name}
                                small={true}
                                renderContent={({ close }) => (
                                    <>
                                    <MenuItem onClick={() => deleteItem(val.day_number, close, val.value)}>
                                        {'—'}
                                    </MenuItem>
                                    {rate.map((rate) => {
                                        return (
                                            <MenuItem key={`${rate} + ${val.name}`} onClick={() => editItem(val.day_number, rate, close)}>
                                                {rate}
                                            </MenuItem>
                                        );
                                    })}
                                    </>
                                )}
                            />
                        ))}
                    </Box>
                </Box>
            }
        </>
    );
};

export default EstimateTable;
